import { useEffect, useState } from 'react';
import { Button, Field, Input, Modal } from '@/ui/primitives';
import {
  useCreateGrupoEconomico,
  useDeleteGrupoEconomico,
  useUpdateGrupoEconomico,
} from '../api';
import type { GrupoEconomico } from '../schemas';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  grupo?: GrupoEconomico | null;
  onSaved?: (grupo: GrupoEconomico) => void;
  onDeleted?: () => void;
}

export function GrupoEconomicoModal({
  open,
  onOpenChange,
  grupo,
  onSaved,
  onDeleted,
}: Props) {
  const [nome, setNome] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useCreateGrupoEconomico();
  const update = useUpdateGrupoEconomico(grupo?.id ?? null);
  const del = useDeleteGrupoEconomico();

  useEffect(() => {
    if (open) {
      setNome(grupo?.nome ?? '');
      setCnpj(grupo?.cnpj ?? '');
      setObservacoes(grupo?.observacoes ?? '');
      setError(null);
    }
  }, [open, grupo]);

  const isEdit = !!grupo;
  const saving = create.isPending || update.isPending;

  async function handleSubmit() {
    setError(null);
    if (!nome.trim()) {
      setError('Nome é obrigatório');
      return;
    }
    try {
      const payload = {
        nome: nome.trim(),
        cnpj: cnpj.trim() || null,
        observacoes: observacoes.trim() || null,
      };
      const result = isEdit
        ? await update.mutateAsync(payload)
        : await create.mutateAsync(payload);
      onSaved?.(result);
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha ao salvar grupo econômico');
    }
  }

  async function handleDelete() {
    if (!grupo) return;
    if (
      !window.confirm(
        `Excluir o grupo "${grupo.nome}"? Os clientes vinculados ficarão sem grupo.`,
      )
    )
      return;
    try {
      await del.mutateAsync(grupo.id);
      onDeleted?.();
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha ao excluir');
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? 'Editar grupo econômico' : 'Novo grupo econômico'}
      size="md"
      footer={
        <>
          {isEdit && (
            <Button
              variant="ghost"
              onClick={handleDelete}
              disabled={del.isPending}
              className="mr-auto text-danger"
            >
              Excluir
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      <Field label="Nome" htmlFor="ge-nome" required>
        <Input
          id="ge-nome"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Ex.: Grupo Fazenda Boa Esperança"
          autoFocus
        />
      </Field>
      <Field label="CNPJ" htmlFor="ge-cnpj" hint="Opcional — somente se houver holding/controladora">
        <Input
          id="ge-cnpj"
          value={cnpj}
          onChange={(e) => setCnpj(e.target.value)}
          placeholder="00.000.000/0000-00"
        />
      </Field>
      <Field label="Observações" htmlFor="ge-obs">
        <textarea
          id="ge-obs"
          className="rounded-md border border-border bg-elevated px-3 py-2 text-sm text-text outline-none focus:border-accent"
          rows={3}
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
        />
      </Field>
      {error && <p className="text-sm text-danger">{error}</p>}
    </Modal>
  );
}
